import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getCoursesBySkillPath,
  selectSkillPathCourses,
} from "../../redux/reducers/courseReducer";
import {
  getSkillPath,
  getAllSkillPathGoals,
  getAllSkillPathSkills,
} from "../../redux/reducers/skillPathReducer";
import {
  SFGridContainer,
  SFGridItem,
  SFContentContainer,
  SFStack,
  SFLoadingPage,
  SFTabs,
  SFTabPanel,
  SFUseStyled,
  SFEmptyPlaceholder,
} from "@sf-core";
import CourseList from "../../components/list";
import SkillPathDetails from "../../components/basicDetails/skillPathDetails";
import MenuVerticalTabs from "../../components/domainPage/menuVerticalTabs";
import { getThumbnailForSkillpath } from "../../helpers/getThumbnailFunction";

const { styled } = SFUseStyled();

const StyledGridItem = styled(SFGridItem, {
  shouldForwardProp: (prop) => prop !== "scrollable",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("lg")]: {
    minHeight: "700px",
  },
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  marginTop: theme.spacing(3) + "!important",
  marginBottom: theme.spacing(2) + " !important",
  paddingRight: theme.spacing(1.5),
  height: 0,
  flex: "1 0 auto",
  overflowX: "hidden",
  ...theme.scrollable,
  minHeight: "300px",
}));

const SFStyledStack = styled(SFStack)(({ theme }) => ({
  minHeight: "250px",
  height: "100%",
}));

const SkillPathScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  let { id } = useParams();
  id = id.toLowerCase();
  const [menuTab, setMenuTab] = useState(0);

  const skillPath = useSelector((state) =>
    state.skillPaths.find((x) => x.id === id)
  );

  const courses = useSelector((state) => selectSkillPathCourses(state, id));

  const skillpathTabs = [
    { label: t("skillpathScreen.goals") },
    { label: t("skillpathScreen.skills") },
  ];

  useEffect(() => {
    dispatch(getSkillPath(id));
  }, [id]);

  useEffect(() => {
    dispatch(getAllSkillPathGoals(id));
    dispatch(getAllSkillPathSkills(id));
    dispatch(getCoursesBySkillPath(id));
  }, []);

  return (
    <SFContentContainer
      backgroundImage={skillPath?.backgroundImage}
      showTopMargin
      showSideMargins
    >
      <>
        {skillPath?.name ? (
          <SFGridContainer sx={{ flex: 1 }} alignItems="stretch" spacing={10}>
            <SFGridItem xs={12} lg={6}>
              <SFStack spacing={6} height="100%">
                {skillPath && (
                  <SkillPathDetails item={skillPath} courses={courses} />
                )}
                <SFTabs
                  items={skillpathTabs}
                  value={menuTab}
                  handleChange={(index) => setMenuTab(index)}
                />
                <StyledStack>
                  <SFTabPanel index={0} value={menuTab}>
                    {skillPath?.goals?.length > 0 ? (
                      <>
                        {skillPath.goals.map((item) => (
                          <MenuVerticalTabs
                            key={item.id}
                            item={item}
                            isSkillpathTab={true}
                            customThumbnail={getThumbnailForSkillpath(item.id)}
                          />
                        ))}
                      </>
                    ) : (
                      <SFStyledStack justifyContent="center">
                        <SFEmptyPlaceholder
                          message={t("skillpathScreen.goalsPlaceholder")}
                        />
                      </SFStyledStack>
                    )}
                  </SFTabPanel>
                  <SFTabPanel index={1} value={menuTab}>
                    {skillPath?.skills?.length > 0 ? (
                      <>
                        {skillPath.skills.map((item) => (
                          <MenuVerticalTabs
                            key={item.id}
                            item={item}
                            isSkillpathTab={true}
                            customThumbnail={getThumbnailForSkillpath(item.id)}
                          />
                        ))}
                      </>
                    ) : (
                      <SFStyledStack justifyContent="center">
                        <SFEmptyPlaceholder
                          message={t("skillpathScreen.skillsPlaceholder")}
                        />
                      </SFStyledStack>
                    )}
                  </SFTabPanel>
                </StyledStack>
              </SFStack>
            </SFGridItem>
            <StyledGridItem xs={12} lg={6}>
              {courses.length === 0 ? (
                <SFLoadingPage />
              ) : (
                <CourseList
                  domain={{}}
                  items={courses}
                  skillPathLevelId={""}
                  courseDescriptionLines={6}
                />
              )}
            </StyledGridItem>
          </SFGridContainer>
        ) : (
          <SFLoadingPage />
        )}
      </>
    </SFContentContainer>
  );
};

export default SkillPathScreen;
